/**
 * PerfectPixel by WellDoneCode — pixel-perfect overlay for web design
 *
 * A single WAR entry exposes `assets/**`, `styles/**` and `settings.html` to every
 * origin with no `use_dynamic_url` — so its icon set, content stylesheet and options
 * page are all fetchable from any page at a fixed URL.
 *
 * The 128px icon is the cleanest signal: an image loads through `probeImage` with no
 * CORS grant, so it survives a restrictive page CSP.
 *
 * Manifest verified against version 3.1.1.3.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'dkaagdgjmgdmbnecmcefdhjekcoceebi';

export const perfectPixel: ExtensionDefinition = {
  id: ID,
  name: 'PerfectPixel by WellDoneCode (pixel perfect)',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 300_000,
  notes:
    'Exposes assets, styles and settings.html to every origin with no dynamic URL. ' +
    'The icon loads without a CORS grant; detection is conclusive.',
  signals: [
    {
      id: 'war-icon-128',
      kind: 'web-accessible-resource',
      description:
        'Load assets/images/icons/128.png via <img> — the toolbar icon exposed to every ' +
        'origin; needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'assets/images/icons/128.png'),
    },
    {
      id: 'war-content-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch styles/content.css — the overlay stylesheet injected into pages, a second ' +
        'independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'styles/content.css'),
    },
    {
      id: 'war-settings-html',
      kind: 'web-accessible-resource',
      description: 'Fetch settings.html — the options page, explicitly declared as a WAR.',
      weight: 0.5,
      run: () => probeFetch(ID, 'settings.html'),
    },
  ],
};
